import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  ReactNode,
  useRef,
} from "react";
import { User, onAuthStateChanged } from "firebase/auth";
import { useQueryClient } from "@tanstack/react-query";
import firebaseAuth from "../services/firebase";
import { useCookie } from "@/hooks/useCookie";
import { COOKIES, POSTHOG, USER_INSTRUCTIONS_WRITE_KEY_ROOT } from "@/constants";
import { DASHBOARD_FETCH_KEY_ROOTS } from "@/constants/dashboardQueryKeys";
import { ONBOARDING_QUERY_ROOT } from "@/hooks/useOnboardingQueries";
import { useUser } from "@/hooks/useUser";
import type { SarasGlobalCookie, UserCookie } from "@/types/cookies";
import { useWarehouse } from "@/hooks/useWarehouse";
import { useSampleQuestions } from "@/hooks/useSample";
import { IQAccess, useRbac } from "@/hooks/useRbac";
import { getCookie } from "@/utils/cookies";
import { useProductFruits } from "@/hooks/useProductFruits";
import { AppError, RoleAccessError } from "@/types/error";
import { clearEvaluationJobStorage } from "@/contexts/evaluationJobStorage";
import { setSentryUserContext, clearSentryUserContext } from "@/config/sentry.config";
import { BOOT_MARKS, markBoot } from "@/helpers/bootMarks";
import { usePostHogTracking } from "@/hooks/usePostHogTracking";
import {
  IS_LOCAL_DEV,
  LOCAL_DEV_USER,
  LOCAL_DEV_FIREBASE_USER,
  resolveLocalDevRoleAccess,
} from "@/config/localDev";
import { isExplicitLogout } from "@/helpers/auth.helpers";

export interface IAuthContextType {
  currentUser?: User | null;
  loading?: boolean;
  userDetails?: any;
  getUserDetails?: any;
  setUserDetails?: any;
  removeUserDetails?: any;
  sarasGlobal?: SarasGlobalCookie | null;
  updateSarasGlobalCookie?: any;
  removeSarasGlobalCookie?: any;
  authTokenCookie?: any;
  updateAuthTokenCookie?: any;
  removeAuthTokenCookie?: any;
  userCookie?: any;
  updateUserCookie?: any;
  removeUserCache?: any;
  warehouse?: any;
  warehouseLoading?: boolean;
  warehouseError?: any;
  createWarehouseSuccess?: boolean;
  getNewProcessingChat?: () => string;
  setNewProcessingChat?: (chatId: string) => void;
  sampleQuestions?: any;
  sampleQuestionsLoading?: boolean;
  sampleQuestionsError?: any;
  roleAccess?: IQAccess;
  roles?: any;
  permissions?: any;
  roleAccessLoading?: boolean;
  roleAccessPending?: boolean;
  roleAccessError?: RoleAccessError | AppError;
  disableComposer?: boolean;
  setDisableComposer?: any;
}

const AuthContext = createContext<IAuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * AuthProvider is a context provider component that manages
 * authentication and user-related state and actions for its
 * children components. It handles user authentication state
 * changes, loading states, and provides utility functions
 * to manage user details, chat and message states, and
 * cookies related to authentication.
 *
 * It uses Firebase authentication for managing auth state
 * changes, and provides a context value with various state
 * variables and functions to interact with and manipulate
 * user and chat data.
 *
 * The provider ensures that its children are rendered only
 * when the loading state is false, indicating that the
 * authentication state has been resolved.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const { identifyUser, resetUser, trackEvent, group } = usePostHogTracking();

  const [sarasGlobal, updateSarasGlobalCookie, removeSarasGlobalCookie] =
    useCookie<SarasGlobalCookie>(COOKIES.saras_global, { sarasDomain: true });
  const [authTokenCookie, updateAuthTokenCookie, removeAuthTokenCookie] =
    useCookie<string>(COOKIES.AUTH_TOKEN);
  const [userCookie, updateUserCookie, removeUserCache] = useCookie<UserCookie>(
    COOKIES.cacheKeysEnum.saras_user_details
  );

  const [disableComposer, setDisableComposer] = useState(false);

  const {
    userDetails,
    getUserDetails,
    setUserDetails,
    removeUserDetails,
  }: any = useUser();

  const newProcessingChatRef = useRef<string>("");
  const hadUserRef = useRef(false);
  const themeAppliedSessionKey = "theme_applied_in_session";
  // Stable identities (ref-backed) so they don't churn the memoized context value.
  const getNewProcessingChat = useCallback(() => {
    return newProcessingChatRef.current;
  }, []);

  const setNewProcessingChat = useCallback((chatId: string) => {
    newProcessingChatRef.current = chatId;
  }, []);
  // Gate on the effective user (synthetic in localdev) so /chats/sample loads
  // against the local backend under `pnpm local`, where Firebase auth never fires.
  const localDevSessionActive = IS_LOCAL_DEV && !isExplicitLogout();
  const effectiveCurrentUser = localDevSessionActive
    ? LOCAL_DEV_FIREBASE_USER
    : currentUser;
  const { sampleQuestions, sampleQuestionsLoading, sampleQuestionsError } =
    useSampleQuestions(effectiveCurrentUser);

  //@ts-ignore
  const { 
    data: roleAccess, 
    roles,
    permissions,
    isLoading: roleAccessLoading, 
    isPending: roleAccessPending,
    error: roleAccessError,
   } = useRbac(currentUser, authTokenCookie);

   useEffect(() => {
    if(!!userDetails && !authTokenCookie){
      const token = getCookie<string>(COOKIES.AUTH_TOKEN);
      if(token){
        updateAuthTokenCookie(token);
      }
    }
   }, [userDetails, authTokenCookie]);

  /**
   * Boot calls that fired while the AUTH_TOKEN cookie was stale/expired come back
   * 401 and — with `staleTime: Infinity` / `refetchOnMount: false` — stay errored
   * until a manual reload. When a NEW token value lands (session restore or the
   * interceptor's exchange finishing), retry just those errored queries.
   * The ref skips the initial mount so a normal warm boot invalidates nothing.
   */
  const queryClient = useQueryClient();
  const previousAuthTokenRef = useRef<string | null>(authTokenCookie);
  useEffect(() => {
    const previousToken = previousAuthTokenRef.current;
    previousAuthTokenRef.current = authTokenCookie;
    if (!authTokenCookie || previousToken === authTokenCookie) return;
    queryClient.invalidateQueries({
      predicate: (query) => query.state.status === "error",
    });
  }, [authTokenCookie, queryClient]);

  /**
   * Drop cached dashboard data (every `DASHBOARD_FETCH_KEY_ROOTS` family: spec,
   * widget batches, per-widget queries, the admin-only creation conversation) when
   * the signed-in Firebase identity changes. It is all per-identity: the
   * conversation cache holds admin-only message text (`iq:admin:dashboard:write`)
   * and query results are scoped to the caller. Logout navigates in-SPA without clearing
   * react-query, so the next user on this tab could otherwise be served the
   * previous user's payload until staleTime lapses. Keyed on the uid, not the
   * token (which rotates on refresh); the ref skips the initial mount and the
   * null → first-user sign-in. The same transition also drops every Settings ›
   * Instructions write mutation (pending or not yet shown), which carries the
   * previous user's instruction text.
   */
  const currentUid = currentUser?.uid ?? null;
  const previousUidRef = useRef<string | null>(currentUid);
  useEffect(() => {
    const previousUid = previousUidRef.current;
    previousUidRef.current = currentUid;
    if (previousUid === null || previousUid === currentUid) return;
    queryClient.removeQueries({
      predicate: (query) => DASHBOARD_FETCH_KEY_ROOTS.has(String(query.queryKey[0])),
    });
    queryClient.removeQueries({
      predicate: (query) => query.queryKey[0] === ONBOARDING_QUERY_ROOT,
    });
    // Settings > Instructions writes: a pending or not-yet-shown save carries the
    // previous user's text and error, and must never surface for the next user.
    const mutationCache = queryClient.getMutationCache();
    mutationCache
      .findAll({ mutationKey: [USER_INSTRUCTIONS_WRITE_KEY_ROOT] })
      .forEach((mutation) => mutationCache.remove(mutation));
  }, [currentUid, queryClient]);
  //@ts-ignore
  const {
    data: warehouse,
    isLoading: warehouseLoading,
    error: warehouseError,
    createWarehouseSuccess,
  } = useWarehouse(userDetails, currentUser);

  useProductFruits(userDetails, !!roleAccess?.APP_PERMISSION && !!userDetails);

  useEffect(() => {
    if (currentUser) {
      hadUserRef.current = true;
    }
    if (currentUser && (userDetails?.userId || userDetails?.company?.companyId)) {
      setSentryUserContext({
        userId: userDetails?.userId,
        companyId: userDetails?.company?.companyId,
      });

      identifyUser(userDetails?.userId, {
        userId: userDetails?.userId,
        companyId: userDetails?.company?.companyId,
        companyName: userDetails?.company?.name,
        email: userDetails?.email,
      });

      // Company-level group analytics (PRD M4: usage/adoption at the company
      // level). Rolls every subsequent event up under this company.
      if (userDetails?.company?.companyId) {
        group(POSTHOG.GROUPS.COMPANY, String(userDetails.company.companyId), {
          name: userDetails?.company?.name,
        });
      }

      const currentTheme = localStorage.getItem('sarasiq-theme') || 'light';

      if (!sessionStorage.getItem(themeAppliedSessionKey)) {
        trackEvent('theme_applied', {
          theme: currentTheme,
        });
        sessionStorage.setItem(themeAppliedSessionKey, 'true');
      }
    } else if (!currentUser && hadUserRef.current) {
      // Only clean up on a real user → null transition (sign-out), not on
      // initial render or while auth / userDetails is still resolving.
      hadUserRef.current = false;
      clearSentryUserContext();
      resetUser();
      sessionStorage.removeItem(themeAppliedSessionKey);
      // A job id left tracking here would let the next signed-in user on this
      // machine/tab resume polling a run — and its result — that isn't theirs.
      clearEvaluationJobStorage();
    }
  }, [currentUser, userDetails?.userId, userDetails?.company?.companyId, identifyUser, resetUser, trackEvent, group]);

  useEffect(() => {
    // Subscribe to auth state changes
    const unsubscribe = onAuthStateChanged(firebaseAuth.getAuth(), (user) => {
      // Boot instrumentation (P4) — marks only. This is the moment the whole
      // provider tree unblocks (`{!loading && children}` below), so it bounds
      // the idle window before the API burst.
      if (user) markBoot(BOOT_MARKS.AUTH_CONTEXT_USER);
      setCurrentUser(user);
      setLoading(false);
    });

    // Cleanup subscription on unmount
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!userDetails) {
      getUserDetails();
    }
    if (
      currentUser &&
      userDetails?.userId &&
      sarasGlobal?.userId &&
      userDetails.userId !== sarasGlobal.userId
    ) {
      removeUserDetails();
      removeUserCache();
      removeSarasGlobalCookie();
      removeAuthTokenCookie();
    }
  }, [userDetails, sarasGlobal, currentUser]);

  // Local-dev bypass: expose a synthetic authenticated session so the app
  // boots against only the local backend (localhost:8000) without the accounts
  // login flow, dev-user-service, or dev-subscription-service. The internal
  // auth hooks above keep their real (null) state in this mode, so they never
  // fire network calls at the dev services — we only override what the UI reads
  // via useAuth(). Inert unless the `localdev` build mode is active (`pnpm local`
  // → `import.meta.env.MODE === "localdev"`, surfaced as IS_LOCAL_DEV in
  // src/config/localDev.ts). There is no `VITE_LOCAL_DEV` env var.
  const effectiveUserDetails = localDevSessionActive ? LOCAL_DEV_USER : userDetails;
  const effectiveRoleAccess = localDevSessionActive
    ? resolveLocalDevRoleAccess()
    : roleAccess;
  const effectiveLoading = localDevSessionActive ? false : loading;

  // Memoize the context value so consumers (chat shell, composer, every
  // <ChatResponse>) don't re-render on unrelated AuthProvider re-renders. This is a
  // high-blast-radius context — behaviour is identical, only the object identity is
  // stabilized. Dep list mirrors every field below exactly.
  const value = useMemo<IAuthContextType>(() => ({
    currentUser: effectiveCurrentUser,
    loading: effectiveLoading,
    userDetails: effectiveUserDetails,
    getUserDetails,
    setUserDetails,
    removeUserDetails,
    sarasGlobal,
    updateSarasGlobalCookie,
    removeSarasGlobalCookie,
    authTokenCookie,
    updateAuthTokenCookie,
    removeAuthTokenCookie,
    userCookie,
    updateUserCookie,
    removeUserCache,
    warehouse,
    warehouseLoading,
    warehouseError,
    createWarehouseSuccess,
    sampleQuestions,
    sampleQuestionsLoading,
    sampleQuestionsError,
    getNewProcessingChat,
    setNewProcessingChat,
    roleAccess: effectiveRoleAccess,
    roles,
    permissions,
    roleAccessLoading,
    roleAccessPending,
    roleAccessError,
    disableComposer,
    setDisableComposer,
  }), [
    effectiveCurrentUser,
    effectiveLoading,
    effectiveUserDetails,
    getUserDetails,
    setUserDetails,
    removeUserDetails,
    sarasGlobal,
    updateSarasGlobalCookie,
    removeSarasGlobalCookie,
    authTokenCookie,
    updateAuthTokenCookie,
    removeAuthTokenCookie,
    userCookie,
    updateUserCookie,
    removeUserCache,
    warehouse,
    warehouseLoading,
    warehouseError,
    createWarehouseSuccess,
    sampleQuestions,
    sampleQuestionsLoading,
    sampleQuestionsError,
    getNewProcessingChat,
    setNewProcessingChat,
    effectiveRoleAccess,
    roles,
    permissions,
    roleAccessLoading,
    roleAccessPending,
    roleAccessError,
    disableComposer,
    setDisableComposer,
  ]);

  const renderChildren = localDevSessionActive ? !effectiveLoading : !loading;

  return (
    <AuthContext.Provider value={value}>
      {renderChildren && children}
    </AuthContext.Provider>
  );
}
